import { useMemo } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { SortableColumnRow } from "./SortableColumnRow";
import type { ColumnMapping } from "../UnitImportWizard";

interface Props {
  headers: string[];
  mapping: ColumnMapping;
  columnOrder: number[];
  sovLines: any[];
  onMappingChange: (colIdx: number, value: string) => void;
  onOrderChange: (order: number[]) => void;
}

export const ColumnMappingList = ({ headers, mapping, columnOrder, sovLines, onMappingChange, onOrderChange }: Props) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Track which SOV lines are already used so we can indicate them
  const usedSovIds = useMemo(() => {
    const used = new Set<string>();
    Object.values(mapping).forEach(v => {
      if (v !== "unit_number" && v !== "floor" && v !== "skip") used.add(v);
    });
    return used;
  }, [mapping]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = columnOrder.indexOf(Number(active.id));
    const newIndex = columnOrder.indexOf(Number(over.id));
    onOrderChange(arrayMove(columnOrder, oldIndex, newIndex));
  };

  const sortableIds = columnOrder.map(String);

  return (
    <div className="max-h-[400px] overflow-y-auto rounded-md border p-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-1">
            {columnOrder.map((colIdx) => {
              const headerText = headers[colIdx]?.trim() || `Column ${colIdx + 1}`;
              return (
                <SortableColumnRow
                  key={colIdx}
                  id={String(colIdx)}
                  colIdx={colIdx}
                  headerText={headerText}
                  value={mapping[colIdx] || "skip"}
                  sovLines={sovLines}
                  usedSovIds={usedSovIds}
                  onChange={onMappingChange}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};
